import { pdf } from '@react-pdf/renderer';
import React from 'react';
import { getAdminInvoicesForOrganization, Invoice } from 'api/Auth/getInvoicesForOrganization';
import SubscriptionReportPDF, { OrgReportData, AthleteReportData } from './SubscriptionReportPDF';

function formatReportDate(d: Date): string {
    return d.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
}

function safeFilename(name: string): string {
    return name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/** Derive the earliest agreement date from invoice list, falling back to today. */
function deriveAgreementDate(invoices: Invoice[]): string {
    if (!invoices.length) return formatReportDate(new Date());
    const earliest = Math.min(...invoices.map((i) => i.created).filter(Boolean));
    return formatReportDate(new Date(earliest * 1000));
}

export interface GenerateOrgReportOptions {
    orgUuid: string;
    orgName: string;
    ownerName?: string | null;
    ownerEmail?: string | null;
    ownerPhone?: string | null;
    subscriptionTier?: string | null;
    subscriptionStatus?: string | null;
    stripeCustomerId?: string | null;
}

export async function generateOrgSubscriptionReport(opts: GenerateOrgReportOptions): Promise<void> {
    // Fetch all invoices (up to 1000).
    // Admin users will get a 403 from the org-scoped invoice endpoint; we catch it and set
    // invoicesFetchFailed so the PDF renders a helpful note instead of leaving the table blank.
    let invoices: Invoice[] = [];
    let invoicesFetchFailed = false;
    try {
        const res = await getAdminInvoicesForOrganization(opts.orgUuid, 1000, 1, 'all');
        invoices = res.data?.data ?? [];
        // Sort oldest first
        invoices.sort((a, b) => a.created - b.created);
    } catch {
        invoicesFetchFailed = true;
    }

    const now = new Date();
    const data: OrgReportData = {
        entityType: 'organization',
        entityName: opts.orgName,
        ownerName: opts.ownerName,
        ownerEmail: opts.ownerEmail,
        ownerPhone: opts.ownerPhone,
        subscriptionTier: opts.subscriptionTier,
        subscriptionStatus: opts.subscriptionStatus,
        stripeCustomerId: opts.stripeCustomerId,
        agreementDate: invoicesFetchFailed ? formatReportDate(now) : deriveAgreementDate(invoices),
        reportGeneratedAt: formatReportDate(now),
        invoices,
        invoicesFetchFailed,
    };

    const blob = await pdf(<SubscriptionReportPDF data={data} />).toBlob();
    const filename = `subscription_report_${safeFilename(opts.orgName)}_${now
        .toISOString()
        .slice(0, 10)}.pdf`;
    triggerDownload(blob, filename);
}

export interface GenerateAthleteReportOptions {
    athleteName: string;
    serialNumber?: string | null;
    organizationName?: string | null;
    subscriptionStatus?: string | null;
    subscriptionStartedAt?: string | null;
    assignmentType?: string;
}

export async function generateAthleteSubscriptionReport(
    opts: GenerateAthleteReportOptions,
): Promise<void> {
    const now = new Date();

    const agreementDate = opts.subscriptionStartedAt
        ? formatReportDate(new Date(opts.subscriptionStartedAt))
        : formatReportDate(now);

    const data: AthleteReportData = {
        entityType: 'athlete',
        entityName: opts.athleteName,
        serialNumber: opts.serialNumber,
        organizationName: opts.organizationName,
        subscriptionStatus: opts.subscriptionStatus,
        subscriptionStartedAt: opts.subscriptionStartedAt
            ? formatReportDate(new Date(opts.subscriptionStartedAt))
            : undefined,
        assignmentType: opts.assignmentType,
        agreementDate,
        reportGeneratedAt: formatReportDate(now),
    };

    const blob = await pdf(<SubscriptionReportPDF data={data} />).toBlob();
    const filename = `subscription_report_${safeFilename(opts.athleteName)}_${now
        .toISOString()
        .slice(0, 10)}.pdf`;
    triggerDownload(blob, filename);
}
